import {
  Box,
  Card,
  Heading,
  IconButton,
  Link,
  Text,
  VStack,
} from '@chakra-ui/react'
import { Pencil } from 'lucide-react'
import React from 'react'

import { createTranslator, type TranslateFn } from '@mangovoice/mango-ui'

export interface PhonePermissionPbx {
  pbxId: number
  pbxName: string
  groupNames: string[]
}

export interface PhonePermissionsSectionProps {
  pbxPermissions: PhonePermissionPbx[]
  isLoading?: boolean
  canEdit?: boolean
  onEditPbx?: (pbxId: number) => void
  onViewPbx?: (pbxId: number) => void
  t?: TranslateFn
}

export const PhonePermissionsSection: React.FC<
  PhonePermissionsSectionProps
> = ({
  pbxPermissions,
  isLoading = false,
  canEdit = false,
  onEditPbx,
  onViewPbx,
  t,
}) => {
  const tr = React.useMemo(() => createTranslator(t), [t])
  return (
    <Card.Root boxShadow="sm" width="100%">
      <Card.Header p={4}>
        <Heading as="h2" fontSize="lg" fontWeight="semibold">
          {tr('users.phonePermissions.heading', 'Phone Permissions')}
        </Heading>
      </Card.Header>
      <Card.Body p={4} pt={0}>
        {isLoading ? (
          <Text fontSize="sm" color="fg.muted">
            {tr(
              'users.phonePermissions.loading',
              'Loading phone permissions...',
            )}
          </Text>
        ) : pbxPermissions.length === 0 ? (
          <Text fontSize="sm" color="fg.muted">
            {tr(
              'users.phonePermissions.empty',
              'This user is not assigned to any phone permission groups.',
            )}
          </Text>
        ) : (
          <VStack align="stretch" gap={3}>
            {pbxPermissions.map((permission) => (
              <Box
                key={permission.pbxId}
                alignSelf="stretch"
                px={4}
                py={3}
                bg="bg.subtle"
                borderRadius="md"
                borderWidth="1px"
                borderColor="border.muted"
              >
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  gap={3}
                  mb={2}
                >
                  {onViewPbx ? (
                    <Link
                      fontSize="sm"
                      fontWeight="semibold"
                      color="fg.info"
                      textDecoration="underline"
                      href="#"
                      onClick={(event) => {
                        event.preventDefault()
                        onViewPbx(permission.pbxId)
                      }}
                    >
                      {permission.pbxName}
                    </Link>
                  ) : (
                    <Text
                      fontSize="sm"
                      fontWeight="semibold"
                      color="fg.default"
                    >
                      {permission.pbxName}
                    </Text>
                  )}
                  {canEdit && onEditPbx && (
                    <IconButton
                      size="xs"
                      variant="subtle"
                      colorPalette="brand"
                      aria-label={tr(
                        'users.phonePermissions.editAria',
                        `Edit phone permissions for ${permission.pbxName}`,
                        { pbxName: permission.pbxName },
                      )}
                      onClick={() => onEditPbx(permission.pbxId)}
                    >
                      <Pencil size={14} />
                    </IconButton>
                  )}
                </Box>

                {permission.groupNames.length === 0 ? (
                  <Text fontSize="xs" color="fg.muted">
                    {tr(
                      'users.phonePermissions.noGroupsAssigned',
                      'No groups assigned.',
                    )}
                  </Text>
                ) : (
                  <VStack align="stretch" gap={1}>
                    {permission.groupNames.map((groupName, index) => (
                      <Text
                        key={`${permission.pbxId}-${groupName}-${index}`}
                        fontSize="xs"
                        color="fg.muted"
                      >
                        {groupName}
                      </Text>
                    ))}
                  </VStack>
                )}
              </Box>
            ))}
          </VStack>
        )}
      </Card.Body>
    </Card.Root>
  )
}
